<template>
	<AccessibleSection
		:name="sectionName"
		:tabOrder="chatHeaderTabOrder"
		:class="
			fullView
				? ['header', 'shadow-left', { embedded: store.config.embedded }]
				: 'thread-view-header'
		"
	>
		<div
			:class="
				fullView
					? [
							'message-control',
							'thread-header-control',
							{ 'embedded-message-control': store.config.embedded },
					  ]
					: 'thread-header-side'
			"
			tabindex="-1"
		>
			<div
				v-if="fullView && showDrawerToggle && condensedSize"
				class="drawer-toggle-wrapper"
			>
				<button
					class="drawer-toggle-btn"
					@click="toggleDrawer"
					:aria-label="$t('accessibility_hamburger_menu')"
				>
					<ChevronIcon left type="solid" />
				</button>
			</div>

			<div class="thread-icon-wrapper">
				<ReelIcon type="solid" />
			</div>
			<div class="thread-header-title-wrapper">
				<div class="thread-header-title" :class="{ noSelect: fullView }">
					{{ threadTitle }}
					<EyeSlashIcon
						type="solid"
						v-if="chat.ishidden"
						class="hidden-group-icon"
					/>
				</div>
				<RouterLink
					v-if="parentChat.title || parentChat.displayname"
					class="thread-header-parent"
					:to="`/chat/${chat.parent_chat_id}`"
				>
					<UserGroupIcon type="solid" />
					<span v-text="parentChat.title || parentChat.displayname" />
				</RouterLink>
			</div>

			<div class="thread-header-menus">
				<Menu
					:items="notificationOptions"
					bottom
					:offsetY="12"
					:closeOnSelect="false"
					ref="notificationMenu"
					@select="onNotificationSelect"
					menuClass="thread-notification-menu"
				>
					<div class="header-action">
						<BellSlashIcon
							v-if="isMuted"
							halo
							type="solid"
							class="bell-slash"
						/>
						<BellIcon v-else halo type="solid" />
					</div>
					<template #item="{ item }">
						<div
							class="notification-item"
							:class="{ mute: item.id === 'mute' || item.id === 'unmute' }"
						>
							<span class="notification-label">{{ item.label }}</span>
							<template v-if="item.id === 'mute'">
								<ChevronIcon right type="regular" class="mute-chevron" />
							</template>
							<template v-else-if="item.id === 'unmute'" />
							<template v-else>
								<Spinner
									v-if="savingNotification === item.id"
									class="notification-spinner"
								/>
								<span
									v-else
									class="notification-radio"
									:class="{ checked: selectedNotification === item.id }"
								/>
							</template>
						</div>
					</template>
				</Menu>

				<Menu
					:items="threadMenuOptions"
					:offsetY="8"
					:closeOnSelect="false"
					@select="onThreadMenuSelect"
					ref="threadMenu"
				>
					<div class="header-dots-menu">
						<EllipsisIcon vertical halo type="solid" />
					</div>
					<template #item="{ item }">
						<div
							class="thread-menu-item"
							:class="{
								danger: item.danger,
								'divider-content': item.class === 'divider-above',
							}"
						>
							<div class="thread-menu-item-text">
								<span class="thread-menu-item-label">{{ item.label }}</span>
								<span v-if="item.subtitle" class="thread-menu-item-subtitle">
									{{ item.subtitle }}
								</span>
							</div>
							<Spinner
								v-if="savingMembership === item.id"
								class="thread-menu-spinner"
							/>
						</div>
					</template>
				</Menu>
			</div>

			<CloseIcon
				v-if="!fullView && showClose"
				type="regular"
				class="pointer"
				@click="$emit('close')"
			/>
		</div>
	</AccessibleSection>

	<StandardModal
		v-if="showRenameModal"
		open
		:title="$t('chat_thread_name_label')"
		:confirmText="$t('common_button_save')"
		:confirmProps="{
			disabled: !renameTitle?.trim() || renameTitle.trim() === threadTitle,
			loading: renameLoading,
		}"
		@confirm="renameThread"
		@cancel="showRenameModal = false"
		@close="showRenameModal = false"
		small
	>
		<div class="rename-thread-body">
			<FormItem :label="$t('groupsettings_option_name')">
				<Input
					v-model="renameTitle"
					autoFocus
					selectAll
					@enter="renameThread"
				/>
			</FormItem>
		</div>
	</StandardModal>

	<DeleteModal
		v-if="showDeleteModal"
		@close="showDeleteModal = false"
		@delete="onDeleteThread"
		:title="$t('chat_thread_delete_title')"
		:bodyText="$t('chat_thread_delete_message')"
		:confirmText="$t('chat_thread_delete_confirm')"
		:isProcessing="deleteLoading"
	/>

	<MuteModal
		v-if="showMuteModal"
		:group="true"
		:bubble_id="chatId"
		:titleOverride="$t('icon_menu_mute')"
		@close="showMuteModal = false"
	/>
</template>

<script>
import {
	CloseIcon,
	ReelIcon,
	BellIcon,
	BellSlashIcon,
	ChevronIcon,
	EllipsisIcon,
	EyeSlashIcon,
	UserGroupIcon,
} from '@/icons'
import {
	Input,
	Menu,
	Spinner,
	FormItem,
	StandardModal,
} from '@/components/common'
import { AccessibleSection } from '@/components/common/Accessible'
import DeleteModal from '@/components/DeleteModal'
import MuteModal from '@/components/MuteModal'
import {
	joinThread,
	leaveThread,
	updateThread,
	deleteThread,
} from '@/api/thread'
import {
	markChatAsRead,
	markChatAsUnread,
	updateMembership,
} from '@/api/membership'
import { post } from '@/helpers/api'
import { closeNotificationsForBubble } from '@/helpers/pushNotification'
import store, {
	getMyUserId,
	getChat,
	updateStat,
	getStat,
	getEffectiveMentions,
} from '@/store'
import {
	getMyMembershipForBubble,
	getMembershipsForBubble,
	getNotificationPreference,
} from '@/store/memberships'
import { chatCan, MANAGE_THREAD, THREAD_OWNER } from '@/helpers/permissions'

export default {
	name: 'ThreadViewHeader',

	components: {
		CloseIcon,
		ReelIcon,
		BellIcon,
		BellSlashIcon,
		ChevronIcon,
		EllipsisIcon,
		EyeSlashIcon,
		UserGroupIcon,
		Input,
		Menu,
		Spinner,
		FormItem,
		StandardModal,
		AccessibleSection,
		DeleteModal,
		MuteModal,
	},

	props: {
		chatId: {
			type: Number,
			required: true,
		},
		sectionName: {
			type: String,
			default: 'chat-header',
		},
		showClose: {
			type: Boolean,
			default: false,
		},
		showDrawerToggle: {
			type: Boolean,
			default: false,
		},
		fullView: {
			type: Boolean,
			default: false,
		},
	},

	emits: ['close'],

	data() {
		return {
			store,
			selectedNotification: this.getNotificationPref(),
			savingNotification: null,
			savingMembership: null,
			showMuteModal: false,
			showRenameModal: false,
			renameTitle: '',
			renameLoading: false,
			showDeleteModal: false,
			deleteLoading: false,
			condensedSize: window.innerWidth <= 800,
			cachedTitle: '',
		}
	},

	watch: {
		chat: {
			handler(chat) {
				const title = chat?.title || chat?.displayname
				if (title) this.cachedTitle = title
			},
			immediate: true,
		},
	},

	mounted() {
		this.$onEventBus('membership-update|chat-update', () => {
			this.selectedNotification = this.getNotificationPref()
		})
		this.$onEventBus('condensedSize', (value) => {
			this.condensedSize = value
		})
		this.$onEventBus('open-thread-notification-menu', () => {
			setTimeout(() => {
				this.$refs.notificationMenu?.openMenu()
			}, 0)
		})
	},

	computed: {
		chat() {
			return getChat(this.chatId) || {}
		},
		parentChat() {
			return getChat(this.chat.parent_chat_id) || {}
		},
		threadTitle() {
			return this.chat.title || this.chat.displayname || this.cachedTitle || ''
		},
		isMuted() {
			const membership = getMyMembershipForBubble(this.chatId)
			return membership?.mute ?? false
		},
		isThreadMember() {
			return !!getMyMembershipForBubble(this.chatId)
		},
		isMarkedUnread() {
			return this.chat.marked_unread
		},
		hasUnread() {
			const unreadCount = this.chat.unread ?? 0
			const mentionsCount = getEffectiveMentions(this.chat)
			return unreadCount > 0 || mentionsCount > 0 || this.isMarkedUnread
		},
		canManageThread() {
			const threadMembership = getMyMembershipForBubble(this.chatId)
			if (
				threadMembership?.role === THREAD_OWNER ||
				threadMembership?.role === 'owner'
			) {
				return true
			}
			return chatCan(this.parentChat, MANAGE_THREAD)
		},
		threadMemberCount() {
			return getMembershipsForBubble(this.chatId).length
		},
		notificationOptions() {
			const options = [
				{
					id: 'all',
					label: this.$t('settings_notification_prefs_all_messages'),
				},
				{
					id: 'mentions',
					label: this.$t('settings_notification_prefs_only_mentions'),
				},
				{ id: 'none', label: this.$t('settings_notification_prefs_nothing') },
			]
			if (this.isThreadMember) {
				options.push({
					id: this.isMuted ? 'unmute' : 'mute',
					label: this.isMuted
						? this.$t('icon_menu_unmute')
						: this.$t('icon_menu_mute'),
				})
			}
			return options
		},
		threadMenuOptions() {
			return [
				{
					id: 'members',
					label: this.$t('admin_groups_members_label'),
					subtitle: this.$t(
						'chat_members_count.variable',
						{ count: this.threadMemberCount },
						this.threadMemberCount
					),
				},
				{
					id: this.isThreadMember ? 'leave' : 'join',
					label: this.isThreadMember
						? this.$t('icon_menu_leave_thread')
						: this.$t('icon_menu_join_thread'),
					class: 'divider-above',
				},
				...(this.isThreadMember && this.chat.ishidden
					? [
							{
								id: 'unhide',
								label: this.$t('icon_menu_unhide_thread'),
							},
					  ]
					: []),
				...(!this.fullView
					? [{ id: 'fullview', label: this.$t('chat_thread_open_fullview') }]
					: []),
				...(this.fullView && this.chat.parent_chat_id
					? [{ id: 'splitview', label: this.$t('chat_thread_open_splitview') }]
					: []),
				...(this.canManageThread
					? [{ id: 'rename', label: this.$t('icon_menu_rename_thread') }]
					: []),
				{ id: 'media', label: this.$t('icon_menu_view_media') },
				...(this.isThreadMember
					? [
							{
								id: this.hasUnread ? 'markasread' : 'markasunread',
								label: this.hasUnread
									? this.$t('icon_menu_mark_read')
									: this.$t('icon_menu_mark_unread'),
							},
					  ]
					: []),
				...(this.canManageThread
					? [
							{
								id: 'delete',
								label: this.$t('icon_menu_delete_thread'),
								danger: true,
								class: 'divider-above',
							},
					  ]
					: []),
			]
		},
		chatHeaderTabOrder() {
			if (this.condensedSize) {
				return [
					{ css: '.drawer-toggle-btn', clickOnEnter: true },
					{ css: '.header-action', clickOnEnter: true },
					{ css: '.header-dots-menu', clickOnEnter: true },
				]
			}
			return [
				{ css: '.header-action', clickOnEnter: true },
				{ css: '.header-dots-menu', clickOnEnter: true },
			]
		},
	},

	methods: {
		getNotificationPref() {
			if (!getMyMembershipForBubble(this.chatId)) return 'none'
			const PREF_TO_ID = { ALL: 'all', MENTIONS: 'mentions', NONE: 'none' }
			const pref = getNotificationPreference(this.chatId)
			return PREF_TO_ID[pref] || 'all'
		},

		toggleDrawer() {
			window.EventBus.$emit('toggleDrawer')
		},

		async onNotificationSelect(item, e) {
			if (e) e.stopPropagation()
			if (item.id === 'unmute') {
				this.$refs.notificationMenu.closeMenu()
				try {
					await updateMembership({
						bubble_id: this.chatId,
						mute: false,
						muteuntil: null,
					})
				} catch (e) {
					this.$alert.error(e)
				}
				return
			}

			if (item.id === 'mute') {
				this.$refs.notificationMenu.closeMenu()
				this.showMuteModal = true
				return
			}

			const ID_TO_PREF = { all: 'ALL', mentions: 'MENTIONS', none: 'NONE' }
			const preference = ID_TO_PREF[item.id]
			if (!preference) return

			const previousNotification = this.selectedNotification
			this.selectedNotification = item.id
			this.savingNotification = item.id

			try {
				if (!this.isThreadMember) {
					await joinThread(this.chatId, getMyUserId())
				}
				await store.setNotificationPreference(this.chatId, {
					bubble_id: this.chatId,
					notificationpreference: preference,
				})
			} catch (e) {
				this.selectedNotification = previousNotification
				this.$alert.error(e)
			} finally {
				this.savingNotification = null
			}
		},

		async onThreadMenuSelect(item) {
			if (item.id !== 'join' && item.id !== 'leave') {
				this.$refs.threadMenu.closeMenu()
			}
			switch (item.id) {
				case 'join':
					this.savingMembership = 'join'
					try {
						await joinThread(this.chatId, getMyUserId())
						this.$alert.success(this.$t('chat_thread_joined'))
					} catch (e) {
						this.$alert.error(e)
					} finally {
						this.savingMembership = null
						this.$refs.threadMenu.closeMenu()
					}
					break
				case 'leave':
					this.savingMembership = 'leave'
					try {
						await leaveThread(this.chatId, getMyUserId())
						this.$alert.success(this.$t('common_confirmation_left'))
					} catch (e) {
						this.$alert.error(e)
					} finally {
						this.savingMembership = null
						this.$refs.threadMenu.closeMenu()
					}
					break
				case 'fullview':
					this.$router.push('/chat/' + this.chatId)
					break
				case 'splitview':
					this.$router.push({
						name: 'chatthread',
						params: {
							chat: 'chat',
							id: this.chat.parent_chat_id,
							threadId: this.chatId,
						},
					})
					break
				case 'rename':
					this.renameTitle = this.threadTitle
					this.showRenameModal = true
					break
				case 'markasread':
					await this.markAsRead()
					break
				case 'markasunread':
					await this.markAsUnread()
					break
				case 'unhide':
					try {
						await updateMembership({
							bubble_id: this.chatId,
							ishidden: false,
						})
					} catch (e) {
						this.$alert.error(e)
					}
					break
				case 'members':
					this.$router.push({
						name: 'threadusers',
						params: {
							chat: 'chat',
							id: this.chat.parent_chat_id,
							threadId: this.chatId,
						},
					})
					break
				case 'media':
					this.$router.push({
						name: 'threadmedia',
						params: {
							chat: 'chat',
							id: this.chat.parent_chat_id,
							threadId: this.chatId,
						},
					})
					break
				case 'delete':
					this.showDeleteModal = true
					break
			}
		},

		async markAsRead() {
			const { latest_message_id, mark, unread, unread_mentions } = getStat(
				this.chatId
			)

			if (!latest_message_id) return

			if (this.isMarkedUnread) {
				try {
					await markChatAsRead(this.chatId)
				} catch (e) {
					this.$alert.error(e)
					return
				}
			}

			updateStat(this.chatId, {
				unread: 0,
				unread_mentions: 0,
			})

			try {
				const { data } = await post('/api/v1/bubble.mark', {
					bubble_id: this.chatId,
					message_id: latest_message_id,
				})
				if (data.ok) {
					updateStat(this.chatId, {
						unread: 0,
						unread_mentions: 0,
						mark: latest_message_id,
					})
					closeNotificationsForBubble(this.chatId)
				} else {
					updateStat(this.chatId, {
						unread,
						unread_mentions,
						mark,
					})
				}
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
				updateStat(this.chatId, {
					unread,
					unread_mentions,
				})
			}
		},

		async markAsUnread() {
			try {
				await markChatAsUnread(this.chatId)
			} catch (e) {
				this.$alert.error(e)
			}
		},

		async renameThread() {
			if (
				!this.renameTitle?.trim() ||
				this.renameTitle.trim() === this.threadTitle
			)
				return
			this.renameLoading = true
			try {
				await updateThread(this.chatId, {
					title: this.renameTitle.trim(),
				})
				this.showRenameModal = false
			} catch (e) {
				console.error('Failed to rename thread:', e)
				this.$alert.error(e)
			} finally {
				this.renameLoading = false
			}
		},

		async onDeleteThread() {
			this.deleteLoading = true
			try {
				await deleteThread(this.chatId)
				this.showDeleteModal = false
				if (this.fullView && this.chat.parent_chat_id) {
					this.$router.push('/chat/' + this.chat.parent_chat_id)
				} else {
					this.$emit('close')
				}
			} catch (e) {
				console.error('Failed to delete thread:', e)
				this.$alert.error(e)
			} finally {
				this.deleteLoading = false
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.thread-icon-wrapper {
	width: 32px;
	height: 32px;
	border-radius: 6px;
	background: var(--neutral-100);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	margin-right: 5px;

	.pronto-icon {
		color: var(--neutral-500);
		font-size: 20px;
		transform: rotate(30deg);
	}
}

.thread-header-parent {
	display: flex;
	align-items: center;
	gap: 3px;
	color: var(--neutral-500);
	font-size: 8px;
	letter-spacing: 1px;
	text-transform: uppercase;
	text-decoration: none;
	@include ellipsis;
	&:hover {
		color: var(--neutral-700);
	}
	.pronto-icon {
		font-size: 8px;
		flex-shrink: 0;
	}
}

.thread-header-control {
	height: 60px;
	border-bottom: solid 1px var(--neutral-40040);
	background-color: var(--neutral-0);
	position: relative;
	outline: 0;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 0 16px 0 16px;

	.thread-header-title-wrapper {
		flex: 1;
		min-width: 0;
	}

	.thread-header-title {
		font-size: 18px;
		@include fontFamily(800);
		@include clamp(1);
		.hidden-group-icon {
			font-size: 16px;
			margin-left: 12px;
			color: var(--neutral-500);
		}
	}

	.thread-header-menus {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		gap: 12px;
	}
}

.thread-header-side {
	display: flex;
	align-items: center;
	height: 60px;
	padding: 0 16px;
	border-bottom: 1px solid var(--neutral-40040);
	flex-shrink: 0;
	background: var(--neutral-0);
	gap: 8px;

	.thread-header-title-wrapper {
		flex: 1;
		min-width: 0;
	}

	.thread-header-title {
		font-family: Avenir-Heavy;
		font-size: 16px;
		color: var(--neutral-800);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		.hidden-group-icon {
			width: 16px;
			height: 14px;
			margin-left: 6px;
			vertical-align: middle;
			color: var(--neutral-500);
		}
	}

	.thread-header-menus {
		display: flex;
		align-items: center;
		flex-shrink: 0;
	}

	> .pronto-icon {
		cursor: pointer;
		color: var(--neutral-400);
		font-size: 22px;

		&:hover {
			color: var(--neutral-500);
		}
	}

	:deep(.actions) {
		.pronto-icon {
			color: var(--neutral-400);
		}
	}
}

.header-action {
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;

	.pronto-icon {
		font-size: 18px;
		color: var(--neutral-400);

		&::before {
			opacity: 0;
		}

		&.bell-slash {
			font-size: 18px;
			transform: scale(1.22);
		}
	}

	&:hover .pronto-icon {
		&::before {
			opacity: 0.1;
		}
	}
}

.header-dots-menu {
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;

	.pronto-icon {
		font-size: 16px;
		color: var(--neutral-400);

		&::before {
			opacity: 0;
		}
	}

	&:hover .pronto-icon {
		&::before {
			opacity: 0.1;
		}
	}
}

@media screen and (max-width: 800px) {
	.thread-header-control {
		.drawer-toggle-wrapper {
			position: absolute;
			left: 8px;
			top: 50%;
			transform: translateY(-50%);
			display: flex;
			align-items: center;
			z-index: 2;
		}
		.drawer-toggle-btn {
			padding: 0;
			background: none;
			border: none;
			cursor: pointer;

			.pronto-icon {
				font-size: 20px;
				color: var(--neutral-400);
				&:hover {
					color: var(--neutral-300);
				}
				&:active {
					color: var(--neutral-500);
				}
			}
		}
		.thread-icon-wrapper {
			margin-left: 46px;
		}
		.thread-header-menus {
			padding-right: 0;
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.rename-thread-body {
	padding: 24px;
}

.thread-notification-menu {
	padding-top: 8px;
	padding-bottom: 8px;
	.menu-item {
		min-width: 160px !important;
		padding: 8px 16px !important;
	}

	.notification-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		gap: 16px;

		.notification-label {
			font-family: Avenir-Medium;
			font-size: 14px;
		}

		.notification-spinner {
			font-size: 16px;
		}

		.notification-radio {
			display: inline-block;
			width: 16px;
			height: 16px;
			border: 2px solid var(--neutral-300);
			border-radius: 50%;
			flex-shrink: 0;

			&.checked {
				border: none;
				background-color: white;
				box-shadow: inset 0 0 0 5px var(--teal-300);
			}
		}

		&.mute {
			border-top: 1px solid var(--neutral-200);
			padding-top: 12px;

			.mute-chevron {
				font-size: 12px;
				color: var(--neutral-400);
			}
		}
	}
}

.menu-item.divider-above {
	margin-top: 4px;
	padding-top: 8px;
	background-image: linear-gradient(var(--neutral-200), var(--neutral-200));
	background-size: calc(100% - 32px) 1px;
	background-position: center top;
	background-repeat: no-repeat;
}

.thread-menu-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;

	&.danger {
		color: var(--red-400) !important;
		padding-bottom: 4px;
	}

	&.divider-content {
		padding-top: 4px;
	}

	&:has(.thread-menu-item-subtitle) {
		padding-top: 4px;
	}

	.thread-menu-item-text {
		display: flex;
		flex-direction: column;

		.thread-menu-item-label {
			font-family: Avenir-Medium;
			font-size: 14px;
		}

		.thread-menu-item-subtitle {
			font-family: Avenir-Book;
			font-size: 12px;
			color: var(--neutral-500);
			margin-top: 1px;
		}
	}

	.thread-menu-spinner {
		font-size: 16px;
	}
}

@include overrideScheme('dark') {
	.thread-header-control {
		background-color: var(--neutral-100);
		border-left: 1px solid var(--neutral-40040);
	}
	.thread-header-side {
		background: var(--neutral-100);
	}
	.thread-icon-wrapper {
		background: var(--neutral-0) !important;
	}
	.thread-menu-item.danger {
		color: var(--red-400);
	}
	.thread-notification-menu .notification-item .notification-radio.checked {
		background-color: var(--neutral-0);
	}
}
</style>
